<template>
  <div class="admin-main">

    <div class="header">
      <h2>버그 리포트</h2>
    </div>

    <div class="content">

      <!-- 필터 탭 -->
      <div class="filter-tabs">
        <button 
          v-for="tab in tabs" :key="tab.value"
          :class="['tab', { active: currentTab === tab.value }]"
          @click="changeTab(tab.value)"
        >
          {{ tab.label }}
          <em v-if="tab.count > 0">{{ tab.count }}</em>
        </button>
      </div>

      <!-- 리포트 목록 -->
      <div class="report-list" v-if="filteredReports.length > 0">
        <div 
          class="report-card" 
          v-for="report in filteredReports" :key="report.idx"
          :class="{ selected: selectedReport?.idx === report.idx }"
          @click="selectReport(report)"
        >
          <div class="card-top">
            <span class="status-badge" :class="report.status">
              {{ statusLabel(report.status) }}
            </span>
            <span class="date">{{ formatDate(report.created_at) }}</span>
          </div>
          <div class="card-body">
            <h3>{{ report.location }}</h3>
            <p>{{ report.description }}</p>
          </div>
          <div class="card-bottom">
            <span class="reporter">
              <i class="ph ph-user"></i> {{ report.mb_id }}
            </span>
          </div>
        </div>
      </div>

      <div class="empty" v-else>
        <i class="ph ph-bug-beetle"></i>
        <p>{{ currentTab === 'all' ? '접수된 버그 리포트가 없습니다.' : '해당 상태의 리포트가 없습니다.' }}</p>
      </div>

    </div>

    <!-- 상세 패널 -->
    <transition name="slide">
      <div class="detail-panel" v-if="selectedReport">
        <div class="panel-header">
          <h2>리포트 상세</h2>
          <span @click="selectedReport = null"><i class="ph ph-x"></i></span>
        </div>

        <div class="panel-body">
          <div class="detail-field">
            <label>상태</label>
            <div class="status-actions">
              <button 
                v-for="s in statusOptions" :key="s.value"
                :class="['status-btn', s.value, { active: selectedReport.status === s.value }]"
                @click="updateStatus(selectedReport, s.value)"
              >
                <i :class="s.icon"></i> {{ s.label }}
              </button>
            </div>
          </div>

          <div class="detail-field">
            <label>제보자</label>
            <span>{{ selectedReport.mb_id }}</span>
          </div>

          <div class="detail-field">
            <label>접수일</label>
            <span>{{ formatDate(selectedReport.created_at) }}</span>
          </div>

          <div class="detail-field">
            <label>발생 위치</label>
            <span>{{ selectedReport.location }}</span>
          </div>

          <div class="detail-field">
            <label>문제 설명</label>
            <p class="desc-text">{{ selectedReport.description }}</p>
          </div>

          <div class="detail-field" v-if="selectedReport.admin_memo">
            <label>관리자 메모</label>
            <p class="desc-text">{{ selectedReport.admin_memo }}</p>
          </div>

          <div class="detail-field">
            <label>메모 작성</label>
            <textarea 
              v-model="memoInput" 
              placeholder="처리 내용이나 메모를 작성하세요..."
              rows="3"
            ></textarea>
            <button class="btn-memo" @click="saveMemo" :disabled="!memoInput.trim()">
              <i class="ph ph-note-pencil"></i> 메모 저장
            </button>
          </div>
        </div>

        <div class="panel-footer">
          <button class="btn-delete" @click="deleteReport(selectedReport)">
            <i class="ph ph-trash"></i> 삭제
          </button>
        </div>
      </div>
    </transition>

  </div>
</template>


<script>
import { ref, computed, inject, onMounted } from "vue"

export default {
  name: "adminBugReport",
  setup() {
    const sql_query = inject("sql_query")
    // const member_info = inject("member_info")

    const reports = ref([])
    const selectedReport = ref(null)
    const currentTab = ref("all")
    const memoInput = ref("")

    const statusOptions = [
      { value: "pending",   label: "대기",   icon: "ph ph-clock" },
      { value: "reviewing", label: "검토중", icon: "ph ph-magnifying-glass" },
      { value: "resolved",  label: "완료",   icon: "ph ph-check-circle" },
      { value: "closed",    label: "종료",   icon: "ph ph-x-circle" },
    ]

    const tabs = computed(() => [
      { label: "전체",   value: "all",       count: reports.value.length },
      { label: "대기",   value: "pending",   count: reports.value.filter(r => r.status === "pending").length },
      { label: "검토중", value: "reviewing", count: reports.value.filter(r => r.status === "reviewing").length },
      { label: "완료",   value: "resolved",  count: reports.value.filter(r => r.status === "resolved").length },
      { label: "종료",   value: "closed",    count: reports.value.filter(r => r.status === "closed").length },
    ])

    const filteredReports = computed(() => {
      if (currentTab.value === "all") return reports.value
      return reports.value.filter(r => r.status === currentTab.value)
    })

    function statusLabel(status) {
      const found = statusOptions.find(s => s.value === status)
      return found ? found.label : status
    }

    function formatDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      const h = String(d.getHours()).padStart(2, "0")
      const min = String(d.getMinutes()).padStart(2, "0")
      return `${y}.${m}.${day} ${h}:${min}`
    }

    async function fetchReports() {
      try {
        const res = await sql_query(`
          SELECT * FROM bug_report ORDER BY created_at DESC
        `)
        reports.value = res || []
      } catch (e) {
        console.error(e)
      }
    }

    function changeTab(tab) {
      currentTab.value = tab
    }

    function selectReport(report) {
      selectedReport.value = { ...report }
      memoInput.value = report.admin_memo || ""
    }

    async function updateStatus(report, status) {
      try {
        await sql_query(`
          UPDATE bug_report SET status = '${status}' WHERE idx = ${report.idx}
        `)
        selectedReport.value.status = status
        const target = reports.value.find(r => r.idx === report.idx)
        if (target) target.status = status
      } catch (e) {
        console.error(e)
        alert("상태 변경에 실패했습니다.")
      }
    }

    async function saveMemo() {
      if (!memoInput.value.trim() || !selectedReport.value) return
      try {
        const memo = memoInput.value.replace(/'/g, "''")
        await sql_query(`
          UPDATE bug_report SET admin_memo = '${memo}' WHERE idx = ${selectedReport.value.idx}
        `)
        selectedReport.value.admin_memo = memoInput.value
        const target = reports.value.find(r => r.idx === selectedReport.value.idx)
        if (target) target.admin_memo = memoInput.value
        alert("메모가 저장되었습니다.")
      } catch (e) {
        console.error(e)
        alert("메모 저장에 실패했습니다.")
      }
    }

    async function deleteReport(report) {
      if (!confirm("이 버그 리포트를 삭제하시겠습니까?")) return
      try {
        await sql_query(`DELETE FROM bug_report WHERE idx = ${report.idx}`)
        reports.value = reports.value.filter(r => r.idx !== report.idx)
        selectedReport.value = null
      } catch (e) {
        console.error(e)
        alert("삭제에 실패했습니다.")
      }
    }

    onMounted(() => {
      fetchReports()
    })

    return {
      reports,
      selectedReport,
      currentTab,
      memoInput,
      tabs,
      filteredReports,
      statusOptions,
      statusLabel,
      formatDate,
      changeTab,
      selectReport,
      updateStatus,
      saveMemo,
      deleteReport,
    }
  }
}
</script>


<style scoped>
.admin-main {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: var(--color-background);
}

.admin-main > .header {
  width: 100%;
  height: 60px;
  display: flex;
  justify-content: start;
  align-items: center;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-surface-reverse);
  padding: 16px;
  box-sizing: border-box;
}

.admin-main > .header > h2 {
  font-size: 24px;
  margin: 0;
  padding: 0;
}

.content {
  width: 100%;
  flex: 1;
  padding: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
}

/* 필터 탭 */
.filter-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tab {
  padding: 8px 16px;
  border: 1px solid var(--color-background-light);
  border-radius: 20px;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.tab:hover {
  border-color: var(--color-text-secondary);
}

.tab.active {
  background: var(--color-brand);
  color: white;
  border-color: var(--color-brand);
}

.tab > em {
  font-style: normal;
  font-size: 12px;
  background: rgba(255,255,255,0.2);
  padding: 1px 7px;
  border-radius: 10px;
}

.tab.active > em {
  background: rgba(255,255,255,0.3);
}

/* 리포트 카드 목록 */
.report-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.report-card {
  width: 100%;
  background: var(--color-surface-reverse);
  border: 1px solid var(--color-background-light);
  border-radius: 12px;
  padding: 16px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.report-card:hover {
  border-color: var(--color-text-secondary);
}

.report-card.selected {
  border-color: var(--color-brand);
  box-shadow: 0 0 0 1px var(--color-brand);
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-badge {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 600;
}

.status-badge.pending {
  background: rgba(255, 170, 0, 0.15);
  color: #ffaa00;
}

.status-badge.reviewing {
  background: rgba(60, 130, 246, 0.15);
  color: #3c82f6;
}

.status-badge.resolved {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.status-badge.closed {
  background: rgba(156, 163, 175, 0.15);
  color: #9ca3af;
}

.card-top > .date {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.card-body > h3 {
  margin: 0;
  font-size: 15px;
  color: var(--color-text-primary);
  font-weight: 600;
}

.card-body > p {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

.card-bottom {
  display: flex;
  align-items: center;
}

.reporter {
  font-size: 12px;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 빈 상태 */
.empty {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  color: var(--color-text-secondary);
}

.empty > i {
  font-size: 48px;
  opacity: 0.4;
}

.empty > p {
  font-size: 14px;
  margin: 0;
}

/* 상세 패널 */
.detail-panel {
  position: absolute;
  right: 0;
  top: 0;
  width: 400px;
  height: 100%;
  background: var(--color-background);
  border-left: 1px solid var(--color-background-light);
  display: flex;
  flex-direction: column;
  z-index: 10;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
}

.panel-header {
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-surface-reverse);
}

.panel-header > h2 {
  font-size: 18px;
  margin: 0;
  color: var(--color-text-primary);
}

.panel-header > span {
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s;
}

.panel-header > span:hover {
  background: var(--color-surface-reverse);
}

.panel-header > span > i {
  font-size: 18px;
  color: var(--color-text-primary);
}

.panel-body {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-field > label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-field > span {
  font-size: 14px;
  color: var(--color-text-primary);
}

.desc-text {
  font-size: 14px;
  color: var(--color-text-primary);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

/* 상태 버튼 */
.status-actions {
  display: flex;
  gap: 8px;
}

.status-btn {
  padding: 6px 14px;
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  background: transparent;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
  color: var(--color-text-secondary);
}

.status-btn:hover {
  border-color: var(--color-text-secondary);
}

.status-btn.pending.active {
  background: rgba(255, 170, 0, 0.15);
  color: #ffaa00;
  border-color: #ffaa00;
}

.status-btn.reviewing.active {
  background: rgba(60, 130, 246, 0.15);
  color: #3c82f6;
  border-color: #3c82f6;
}

.status-btn.resolved.active {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
  border-color: #22c55e;
}

.status-btn.closed.active {
  background: rgba(156, 163, 175, 0.15);
  color: #9ca3af;
  border-color: #9ca3af;
}

/* 메모 입력 */
.detail-field > textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid var(--color-background-light);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 13px;
  box-sizing: border-box;
  outline: none;
  resize: vertical;
  min-height: 70px;
  transition: border-color 0.2s;
}

.detail-field > textarea:focus {
  border-color: var(--color-brand);
}

.btn-memo {
  align-self: flex-end;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background: var(--color-brand);
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: opacity 0.2s;
}

.btn-memo:hover {
  opacity: 0.9;
}

.btn-memo:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 패널 푸터 */
.panel-footer {
  padding: 16px;
  border-top: 1px solid var(--color-background-light);
  display: flex;
  justify-content: flex-end;
}

.btn-delete {
  padding: 8px 14px;
  border: 1px solid #ef4444;
  border-radius: 8px;
  background: transparent;
  color: #ef4444;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
}

.btn-delete:hover {
  background: rgba(239, 68, 68, 0.1);
}

/* 슬라이드 트랜지션 */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>